import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';

import ExternalLink from '../../components/Link/ExternalLink';
import { useSiteUrl } from '../../graphql/hooks';

const SuperscriptData = () => {
  const images = useStaticQuery(graphql`
    {
      metaImage: file(
        relativePath: { eq: "assets/img/superscript/superscript-meta.jpg" }
      ) {
        ...localImageHero
      }
      AmberJeanRowanProfile: file(
        relativePath: { eq: "assets/img/superscript/amber-jean-rowan.jpg" }
      ) {
        childImageSharp {
          gatsbyImageData(
            width: 600
            quality: 100
            placeholder: NONE
            layout: CONSTRAINED
          )
        }
      }
      AaronWallace: file(
        relativePath: { eq: "assets/img/superscript/aaron-wallace.jpg" }
      ) {
        childImageSharp {
          gatsbyImageData(
            width: 600
            quality: 100
            placeholder: NONE
            layout: CONSTRAINED
          )
        }
      }
      JackMeeks: file(
        relativePath: { eq: "assets/img/superscript/jack-meeks.jpg" }
      ) {
        childImageSharp {
          gatsbyImageData(
            width: 600
            quality: 100
            placeholder: NONE
            layout: CONSTRAINED
          )
        }
      }
      ThomasConstant: file(
        relativePath: { eq: "assets/img/superscript/thomas-constant.jpg" }
      ) {
        childImageSharp {
          gatsbyImageData(
            width: 600
            quality: 100
            placeholder: NONE
            layout: CONSTRAINED
          )
        }
      }
      TianahHodding: file(
        relativePath: { eq: "assets/img/superscript/tianah-hodding.jpg" }
      ) {
        childImageSharp {
          gatsbyImageData(
            width: 600
            quality: 100
            placeholder: NONE
            layout: CONSTRAINED
          )
        }
      }
    }
  `);
  const SUPERSCRIPT = {
    aaron: {
      bio: (
        <div>
          <p>
            When Aaron Wallace is faced with a challenge, he repeats a mantra:
            ‘Competence equals confidence.’
          </p>
          <p>
            The founder of the eponymous grooming line for black men is always
            trying to stay a step ahead of his fears and not let them get in the
            way of his ambitions. He likes to remind himself that all experts
            were once amateurs and that, more often than not, it’s the fear of
            risk that holds us back, rather than the risk itself.
          </p>
          <p>
            ‘Starting in business is like walking into a dark room,’ he says.
            ‘At first you can&apos;t see anything; you&apos;re fumbling around
            and then eventually you find the light switch and can see what’s
            going on.’
          </p>
        </div>
      ),
      cta: {
        header: 'Cut the negative self-talk',
        superscriptPromo:
          'Superscript champions change-makers and empowers businesses to take risks by making business insurance accessible, intuitive and uncomplicated. Giving entrepreneurs the confidence to go all in.',
        text: (
          <div>
            <p>
              ‘The benefits of positive affirmations are really important in
              countering imposter syndrome. It&apos;s so easy to have negative
              self-talk. And it&apos;s very easy to do it without even realizing
              you&apos;re doing it.
            </p>
            <p>
              ‘I would often catch myself having negative self-talk and, because
              I’ve been reading about it, I’m able to recognize it. In those
              moments, I ask myself: why am I being mean to myself? I’m able to
              catch the negative self-talk and replace it with a positive
              affirmation. Now, whenever negative self-talk comes up, I have a
              positive statement to replace it with.’
            </p>
          </div>
        ),
      },
      metadata: {
        description: `Meet @aaronmwallace, founder of @byaaronwallace. The award-winning brand offers hair and skin products made especially for black men. And it all started from the conversations he had every day with the clients in his south-east London barbershop. This is his story.`,
        image: `${useSiteUrl()}${images.AaronWallace.publicURL}`,
        title: 'Aaron Wallace',
        twitterCard: 'summary_large_image',
      },
      name: 'Aaron Wallace',
      pullquote:
        'There will always be another “job” job, but you have to take a big risk and go for something you really want.',
      section1: {
        header: 'What’s really at stake?',
        text: (
          <div>
            <p>
              For Aaron, the risk isn’t in failing, but in not doing something.
              This is as true today as it was when he first started out in
              business, leaving a steady job in the charity sector to open his
              own barbershop.
            </p>
            <p>
              ‘The very first risk I took was deciding to go from employment
              into self-employment,’ he says. ‘I didn’t see much growth where I
              was and I knew that I wanted to work for myself.’
            </p>
            <p>
              Aaron says that, at the time, he wasn’t indecisive but rather
              scared of the unknown. When it came down to it, he knew he had to
              see where his passions could take him. ‘There will always be
              another “job” job, but there’s so much time you have to take a big
              risk and go for something that you really want.’
            </p>
          </div>
        ),
      },
      section2: {
        header: 'Calculated risks',
        text: (
          <div>
            <p>
              After several successful years running the south London
              barbershop, Aaron pivoted into products. His clients had been
              asking him for recommendations for products for black hair, so he
              saw a gap in the grooming market. Aaron launched{' '}
              <ExternalLink href="https://byaaronwallace.com/">
                his own line
              </ExternalLink>{' '}
              of beard and hair oils, shampoos and conditioners, which is now
              stocked by Liberty, Saks and Asos.
            </p>
            <p>
              Aaron says he thinks of himself as a calculated risk-taker. The
              most important work he’s done in the last few years is cultivating
              greater self-awareness about what holds him back. He had to remind
              himself of this lesson recently after a conversation with
              potential new investors. ‘The knee-jerk reaction was to think:
              what if I can’t keep up with their demands?’ he says.
            </p>
            <p>
              Aaron says he had to ask himself if it was legitimate risks
              causing him to hesitate or a fear of growth. ‘Once you’ve
              alleviated all your doubts, it’s only fear that remains,’ he says.
            </p>
          </div>
        ),
      },
      videoURL: '',
    },
    amber: {
      bio: (
        <div>
          <p>
            Amber Jean Rowan, founder of soon-to-launch wig brand{' '}
            <ExternalLink href="https://amberjeanshop.com/">
              Amber Jean
            </ExternalLink>
            , spent a decade hiding her story. Once she started sharing it,
            however, her life changed in unimaginable ways.
          </p>
          <p>
            When Amber was 15, she developed alopecia, an autoimmune disease
            that causes hair loss. By the time she was 17, she was completely
            bald. Initially, Amber, who’s a model and actress, coped by hiding
            this from the world. She wore wigs, drew on her eyebrows (which she
            lost when she was 19) and wore false lashes. ‘It took me a good few
            years to come to terms with the loss of it,’ she says. ‘The loss of
            myself, essentially.’
          </p>
        </div>
      ),
      cta: {
        header: 'Your story will set you free',
        superscriptPromo:
          'Superscript champions change-makers and empowers businesses to take risks by making business insurance accessible, intuitive and uncomplicated. Giving entrepreneurs the confidence to go all in.',
        text: (
          <div>
            <p>
              ‘The risks I took in walking down the street in India with my bald
              head, or being in front of the camera for the first time without
              my wig, have led to so much self-growth. I had an overwhelming
              feeling of vulnerability, but there was such a sense of joy and
              pride there, too.
            </p>
            <p>
              ‘When I started posting pictures of myself hair-free, my career
              really opened up. The amount of interest I got from casting
              directors and magazines blew me away. I was brought in with open
              arms and offered dream jobs. People want people who have something
              to say.’
            </p>
          </div>
        ),
      },
      metadata: {
        description: `Meet @amberjeanrowan. She set up her first business @hairfreelife in the pandemic. Regardless, she believed in doing what was meaningful – creating conscious hairpieces for the alopecia community and bringing back joy to the hair-buying experience. This is her story.`,
        image: `${useSiteUrl()}${images.AmberJeanRowanProfile.publicURL}`,
        title: 'Amber Jean Rowan',
        twitterCard: 'summary_large_image',
      },
      name: 'Amber Jean Rowan',
      pullquote:
        'There were so many other women going through the same thing that I really wanted to lead by example.',
      section1: {
        header: 'Hair free',
        text: (
          <div>
            <p>
              It took years for Amber to accept herself. The gradual process
              began with her taking her wig off in places she felt comfortable,
              namely in front of loved ones. The acceptance she received gave
              her the courage to do something she would never have entertained
              at the start of her career: be photographed without her wig. ‘I
              found it much easier to be in a controlled setting, like a photo
              shoot, than on the street,’ she says. Her confidence grew with
              each step she took.
            </p>
            <p>
              Amber was getting messages from people who’d seen her photos
              without her wig and she wanted to use her position to create a
              positive space to share their stories. ‘There were so many other
              women going through the same thing that I really wanted to lead by
              example,’ she says. Two years ago, Amber started{' '}
              <ExternalLink href="https://hairfree.life/">
                Hair Free
              </ExternalLink>
              , an online community for others suffering with the condition. ‘I
              was just so fed up with the fact that there were no resources out
              there,’ she says.
            </p>
          </div>
        ),
      },
      section2: {
        header: 'Growth',
        text: (
          <div>
            <p>
              A turning point for Amber came three years ago when she went to
              India for a yoga retreat. ‘I promised myself I wouldn&apos;t wear
              my headpiece, that I’d leave it behind in the box,’ she says.
              Feeling the sun on her scalp and dipping her head under water was
              an overwhelming experience for her.
            </p>
            <p>
              When Amber came back, something had shifted. ‘I was stronger and
              more comfortable with expressing myself, both with wigs and
              without them,’ she says. ‘I was able to look at myself in the
              mirror and feel like me – peaceful with or without.’
            </p>
            <p>
              Now, Amber is gearing up to launch her own line of wigs with Amber
              Jean Shop. The brand will carry hair pieces as well as educational
              resources about alopecia and hair loss. ‘My vision is for it to
              give women going through hair loss practical hair pieces, as well
              as emotional support,’ she says. ‘To be able to be with them
              through the whole journey.’
            </p>
          </div>
        ),
      },
      videoURL: '',
    },
    home: {
      metadata: {
        description: ``,
        image: `${useSiteUrl()}${images.metaImage.publicURL}`,
        title: 'Flip the script',
        twitterCard: 'summary_large_image',
      },
      text: `Running a business will always involve risk. But while too much risk creates volatility, taking the right risks at the right time could be the best decision you'll ever make. We spoke to five change-makers who stepped out of their comfort zones to embrace the unknown and take their small businesses to the next level.`,
      title: 'Flip the script',
    },
    jack: {
      bio: (
        <div>
          <p>
            It started with a piece of driftwood. Jack Meeks, founder of the
            reclaimed home-decor brand{' '}
            <ExternalLink href="https://urura.co.uk/">Urura</ExternalLink>, grew
            up by the sea in Wales and would collect scraps of wood he found on
            the beach. ‘Hoarding, you could call it,’ he says.
          </p>
          <p>
            Jack started small, making decorative pieces, and then worked on a
            bigger project: kitting out his van so he could travel between
            surfing jobs. A distinct style quickly emerged, one infused by
            Jack’s surroundings; diagonal patterns and blue-green colour
            palettes reflect the mountainous coast of North Wales.
          </p>
          <p>
            Four years ago, a friend asked him to make them a piece and it
            ‘snowballed from there’. What started as a hobby turned into a
            career. But just as the business started to get going, Jack’s plans
            took a tumble.
          </p>
        </div>
      ),
      cta: {
        header: 'Feedback will keep you going',
        superscriptPromo:
          'Superscript champions change-makers and empowers businesses to take risks by making business insurance accessible, intuitive and uncomplicated. Giving entrepreneurs the confidence to go all in.',
        text: (
          <div>
            <p>
              ‘One of the biggest things for me is hearing feedback, because you
              know you&apos;re doing the right thing. People really love your
              stuff and it&apos;s enjoyable to make someone feel happy and get
              their reaction. When you spend three weeks making a really unique
              piece of artwork or dining table and you ship it to someone, then
              they open the box and say it’s absolutely made their home,
              it&apos;s the centerpiece of the house, that really moves you. The
              connection I have with the customer is very personal.’
            </p>
          </div>
        ),
      },
      metadata: {
        description: `Meet @jackmeeks, woodwork artist and founder of @urura.home. He was a surf instructor, a tree surgeon and then, after a period of questioning, decided to pursue what he loves. Now Jack lives on the coast, crafting the beautiful and unique from local, reclaimed driftwood. This is his story.`,
        image: `${useSiteUrl()}${images.JackMeeks.publicURL}`,
        title: 'Jack Meeks',
        twitterCard: 'summary_large_image',
      },
      name: 'Jack Meeks',
      pullquote:
        'It was like someone had switched on a light switch. All of a sudden, people were messaging asking for wall art and tables.',
      section1: {
        header: 'Ride it out',
        text: (
          <div>
            <p>
              Over Christmas 2019, Jack’s biggest order came in and he decided
              it was time to double down on the business. At the time, he was
              making pieces out of his shed, so he put his name down for a
              larger workspace.
            </p>
            <p>
              But then the pandemic hit and activity immediately dried up. ‘At
              the time, I thought: well, this is it,’ he says. ‘I didn&apos;t
              get a single email for about a month.’
            </p>
            <p>
              With no orders in the pipeline, the business ground to a halt.
              Urura was also too new to qualify for any of the government grants
              or financial assistance. To make matters worse, Jack now needed to
              make a call on the workspace. If he wanted to keep it, he’d have
              to pay rent at a time when he didn’t have any orders coming in. ‘I
              had to decide whether to pack it all in or try to tread water and
              see what happened.’
            </p>
          </div>
        ),
      },
      section2: {
        header: 'Sunnier times ahead',
        text: (
          <div>
            <p>
              Jack kept going. The silver lining for him was that, other than
              the rent on his studio and his labor, his materials are free.
            </p>
            <p>
              He was able to work on some stock and wait for orders to resume.
              As quickly as everything shut down, business picked up again.
              After the initial shock of the pandemic set in, people stuck at
              home on furlough started decorating. ‘It was like someone had
              switched on a light switch,’ he says. ‘All of a sudden, people
              were messaging asking for wall art and tables.’
            </p>
            <p>
              In recent weeks, Jack took part in the UK’s Young Traders Market
              and won in his category. He’ll soon be competing in the
              semi-finals. ‘Sometimes you just have to keep looking forward to
              get through the hard times,’ he says.
            </p>
          </div>
        ),
      },
      videoURL: '',
    },
    profileOverview: {
      0: {
        image: images.AmberJeanRowanProfile.childImageSharp.gatsbyImageData,
        name: (
          <span>
            Amber <span className="superscript__mob-break" />
            Jean Rowan
          </span>
        ),
        slug: '/partnerships/superscript/amber-jean-rowan',
      },
      1: {
        image: images.AaronWallace.childImageSharp.gatsbyImageData,
        name: (
          <span>
            Aaron <span className="superscript__mob-break" />
            Wallace
          </span>
        ),
        slug: '/partnerships/superscript/aaron-wallace',
      },
      2: {
        image: images.ThomasConstant.childImageSharp.gatsbyImageData,
        name: (
          <span>
            Thomas <span className="superscript__mob-break" />
            Constant
          </span>
        ),
        slug: '/partnerships/superscript/thomas-constant',
      },
      3: {
        image: images.TianahHodding.childImageSharp.gatsbyImageData,
        name: (
          <span>
            Tianah <span className="superscript__mob-break" />
            Hodding
          </span>
        ),
        slug: '/partnerships/superscript/tianah-hodding',
      },
      4: {
        image: images.JackMeeks.childImageSharp.gatsbyImageData,
        name: (
          <span>
            Jack <span className="superscript__mob-break" />
            Meeks
          </span>
        ),
        slug: '/partnerships/superscript/jack-meeks',
      },
    },
    thomas: {
      bio: (
        <div>
          <p>
            Thomas Constant wants you to eat bugs – specifically ones you’ve
            bred in your own kitchen. The founder of{' '}
            <ExternalLink href="https://www.beobia.com/">BeoBia</ExternalLink>,
            a sustainable insect-growing pod, is on a mission to change our
            relationship with what we eat, reduce carbon emissions and reuse
            food waste – all by getting people to breed mealworms in their own
            homes.
          </p>
          <p>
            While the insect protein market is growing, breeding them at home is
            the next frontier. However, the business might never have come to
            life had it not been for a bold move Thomas made at the beginning of
            his career, leaving the traditional path behind for the unknown.
          </p>
        </div>
      ),
      cta: {
        header: 'Find your support systems',
        superscriptPromo:
          'Superscript champions change-makers and empowers businesses to take risks by making business insurance accessible, intuitive and uncomplicated. Giving entrepreneurs the confidence to go all in.',
        text: (
          <div>
            <p>
              ‘Winning accolades consolidated the idea that we must be doing
              something right. At the start of the process, getting into an
              incubator really gave me the skill set to understand what I&apos;m
              doing. It helped me grow as a person and gave me access to the
              kind of support that allowed me to develop this business. I
              wouldn&apos;t have been able to do that if I was by myself.
              Everything from sharing the office space with other small
              businesses to the mentorship really helped. I wouldn&apos;t be
              where I am now without that.’
            </p>
          </div>
        ),
      },
      metadata: {
        description: `Meet @tgconstant, founder of @beobia, a pet food business that reuses our uneaten fruit and veg. After studying design and ecology, he arrived at a choice: take a full-time job or start his own business… and eventually innovate a whole sector. This is his story.`,
        image: `${useSiteUrl()}${images.ThomasConstant.publicURL}`,
        title: 'Thomas Constant',
        twitterCard: 'summary_large_image',
      },
      name: 'Thomas Constant',
      pullquote:
        'On a base level, it seemed like a risk. But I realized: this is what I want, this is the only way.',
      section1: {
        header: 'Gap in the bug market',
        text: (
          <div>
            <p>
              Thomas came up with the DIY food idea while he was studying design
              and technology at Loughborough University. ‘The germ of the idea
              started with me looking at which global issues are predicted to
              damage the world this century,’ he says. Shocked by the
              environmental impact of intensive livestock farming, Thomas was
              drawn towards the innovations happening in alternative proteins.
            </p>
            <p>
              While he found plenty of innovation in meat alternatives, from
              lab-grown burgers to insect protein, he found a gap in the market.
              ‘I wanted to find a way to empower people to grow their own source
              of protein,’ he says. He designed an eco pod in his final year,
              winning the university’s award for best commercially viable idea.
            </p>
          </div>
        ),
      },
      section2: {
        header: 'Fork in the road',
        text: (
          <div>
            <p>
              When Thomas graduated, he started working as a designer in London,
              but the idea was still in the back of his mind. He found himself
              at a fork in the road when he was offered a full-time job at a
              prestigious agency on the same day he found out he’d been accepted
              onto a business incubator programme.
            </p>
            <p>
              ‘On a base level, it seemed like a risk,’ he says. ‘But when you
              think about it – or when you feel it – it becomes less risky. I
              realized: this is what I want, this is the only way.
            </p>
            <p>
              In 2019, BeoBia (which means ‘food for life’ in Gaelic) ran a
              successful Kickstarter campaign, raising £23,000. The feedback
              from the campaign led to Thomas pivoting the company in a new
              direction.
            </p>
            <p>
              The product – the eco pod – remains the same, but rather than
              marketing it to humans, it’s now about creating a sustainable
              solution for feeding pets. ‘I set up BeoBia with a clear mission:
              to do good for people and the planet,’ he says. ‘Sustainability
              will always be at its core.’
            </p>
          </div>
        ),
      },
      videoURL: '',
    },
    tianah: {
      bio: (
        <div>
          <p>
            How does a cult brand following start? With a slide into the DMs.
            Tianah Hodding, founder of micro-sunglass label{' '}
            <ExternalLink href="https://christianahjones.com/">
              CHRISTIANAHJONES
            </ExternalLink>
            , knew how transformative influencers and tastemakers can be for
            fledgling brands when she reached out to the actress and model Kaia
            Gerber over Instagram.
          </p>
          <p>
            ‘I DM’d her,’ she says. ‘Wildly, she responded.’ Gerber loved the
            glasses and their distinctive tinted and pinched look. She wore them
            to Paris Fashion Week in 2018, where the paparazzi shots of her
            wearing them blew up the trend for micro-shades.
          </p>
          <p>
            Tianah was soon flooded with orders – and more requests from
            celebrities. At the same time, however, she had to contend with the
            shadier side of the industry, namely fast-fashion copycats and risky
            financial investments.
          </p>
        </div>
      ),
      cta: {
        header: 'Use a ‘no’ as fuel',
        superscriptPromo:
          'Superscript champions change-makers and empowers businesses to take risks by making business insurance accessible, intuitive and uncomplicated. Giving entrepreneurs the confidence to go all in.',
        text: (
          <div>
            <p>
              Don’t take a “no” as feedback, but use it as motivation instead.
              It’s really easy for one person to say “no” to you and for you to
              assume your idea is terrible. As long as you’ve found your space
              in the market, or even if you just believe you can place yourself
              in the market, then there’s no reason why you shouldn’t be there.
              Just be very persistent, but in the most respectful way.
              Don&apos;t be annoying.’
            </p>
          </div>
        ),
      },
      metadata: {
        description: `Meet Tianah Hodding, thrifter, traveler and collector of vintage fashion pieces. Her mixed passions led her to start the sunglasses brand @christianahjones alongside full-time work. But doing both isn’t easy, especially when surrounded by voices of disbelief – and even more so when living with hearing loss. This is her story.`,
        image: `${useSiteUrl()}${images.TianahHodding.publicURL}`,
        title: 'Tianah Hodding',
        twitterCard: 'summary_large_image',
      },
      name: 'Tianah Hodding',
      pullquote:
        'Don’t take a “no” as feedback, but use it as motivation instead. It’s really easy for one person to say “no” to you and for you to assume your idea is terrible.',
      section1: {
        header: 'Micro-brand',
        text: (
          <div>
            <p>
              Tianah is well versed in the power of social media on fashion and
              retail; her day job is heading up socials for the secondhand
              retail app Vinterior. Running CHRISTIANAHJONES as a side hustle,
              albeit one with global scale, comes with its challenges. ‘I
              struggled to get people on board at first because I wasn’t working
              on it full time,’ she says.
            </p>
            <p>
              She says she gets a lot of judgement for her decision to work in
              this way, but for her it’s a matter of pragmatism. As a
              25-year-old living in London, the security of a full-time job
              isn’t something to be taken lightly. ‘By working, I feel more
              comfortable funding everything,’ she says.
            </p>
          </div>
        ),
      },
      section2: {
        header: 'Copycats',
        text: (
          <div>
            <p>
              An unexpected expense came along quickly. ‘I was popping off so
              quickly that companies like eBay and Alibaba were stealing my
              designs and selling them for knock-off prices,’ she says.
            </p>
            <p>
              Tianah consulted a lawyer but, beyond trademarking her company
              name and registering her designs with HMRC, there was little she
              could do. ‘That was really painful,’ she says.
            </p>
            <p>
              The setbacks haven’t stopped her, though, and Tianah is now in the
              middle of a rebrand. She wants customers to be able to buy her
              collections from a range of online stores, anywhere in the world.
              ‘The long-term vision is to be the go-to eyewear label,’ she says.
            </p>
            <p>
              As for the tiny sunglasses, she’s got her sights set on new
              designs and styles. But in keeping with her ethos, the collections
              themselves will stay small. ‘I want to drop two designs a year,’
              she says.
            </p>
          </div>
        ),
      },
      videoURL: '',
    },
  };
  return {
    SUPERSCRIPT,
  };
};

export default SuperscriptData;
